import React, { memo, type JSX } from 'react';
import {
    MenuItem,
    IconButton,
    Typography,
    Paper,
    TableContainer,
    TableHead,
    Table,
    TableRow,
    TableCell,
    Checkbox,
    InputAdornment,
    FilledInput,
    FormControl,
    TableSortLabel,
    Stack,
    TableBody,
    Button,
    Box,
    Radio,
    CircularProgress,
    Popper,
    Select,
    type SelectChangeEvent,
    type SvgIconProps,
    type PopperProps,
} from '@mui/material';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import { Header } from '@fiji/common/src/types';
import { TableSkeleton } from './TableSkeleton';
import { EmptyState } from '@brightlayer-ui/react-components';
import Add from '@mui/icons-material/Add';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { useIsMount, usePagination, useSelectedIds } from '../hooks';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import { CustomTableLoadMoreProps } from '@fiji/common/src/features/common/commonTypes';
import { CustomTransComponent } from 'components';

type TableFilterValue = string | number | boolean;
type TableFilterOption = { id: TableFilterValue; label?: React.ReactNode };
type TableFilterHeader = Pick<Header, 'accessor'> & { filterSelectionType?: string };

const getTableCellSx = (header: Header, rowData?: any): SxProps<Theme> => ({
    ...(header.minWidth && { minWidth: header.minWidth }),
    ...(header.sx &&
        rowData !== undefined && {
            position: 'relative',
            '&::before': header.sx(rowData),
        }),
});

type TableFilterPopperProps = {
    anchorEl: HTMLElement | null;
    tableBoundary?: HTMLElement | null;
    offset?: [number, number];
    placement?: PopperProps['placement'];
    zIndex: number;
    popupRef: React.Ref<HTMLDivElement>;
    children: React.ReactNode;
};

const TableFilterPopper = ({
    anchorEl,
    tableBoundary,
    offset,
    placement,
    zIndex,
    popupRef,
    children,
}: TableFilterPopperProps): JSX.Element => {
    const [bounds, setBounds] = React.useState({ maxHeight: 0, maxWidth: 0 });
    const offsetY = offset?.[1] ?? 0;

    React.useLayoutEffect(() => {
        if (!anchorEl || !tableBoundary) return;

        const updateBounds = (): void => {
            const tableRect = tableBoundary.getBoundingClientRect();
            const anchorRect = anchorEl.getBoundingClientRect();
            const nextBounds = {
                maxHeight: Math.max(0, Math.min(tableRect.bottom, window.innerHeight) - anchorRect.bottom - offsetY),
                maxWidth: Math.max(0, Math.min(tableRect.right, window.innerWidth) - Math.max(tableRect.left, 0)),
            };
            setBounds((previous) =>
                previous.maxHeight === nextBounds.maxHeight && previous.maxWidth === nextBounds.maxWidth
                    ? previous
                    : nextBounds
            );
        };

        updateBounds();
        document.addEventListener('scroll', updateBounds, true);
        window.addEventListener('resize', updateBounds);
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateBounds);
        observer?.observe(tableBoundary);
        observer?.observe(anchorEl);
        return (): void => {
            document.removeEventListener('scroll', updateBounds, true);
            window.removeEventListener('resize', updateBounds);
            observer?.disconnect();
        };
    }, [anchorEl, tableBoundary, offsetY]);

    const modifiers: NonNullable<PopperProps['modifiers']> = [];
    if (offset) modifiers.push({ name: 'offset', options: { offset } });
    if (tableBoundary) {
        modifiers.push(
            { name: 'flip', enabled: false },
            {
                name: 'preventOverflow',
                options: { boundary: tableBoundary, altAxis: true, tether: false, padding: 0 },
            }
        );
    }

    return (
        <Popper
            ref={popupRef}
            open
            anchorEl={anchorEl}
            placement={tableBoundary ? 'bottom-start' : placement}
            modifiers={modifiers}
            sx={{
                zIndex,
                ...(tableBoundary && {
                    ...bounds,
                    overflow: 'visible',
                }),
                '& > .table-search-bar': {
                    position: 'relative',
                    top: '0 !important',
                    // Keep scrolling inside the Paper without clipping its elevation shadow.
                    ...(tableBoundary && {
                        maxHeight: 'inherit',
                        maxWidth: 'inherit',
                        overflow: 'auto',
                        overscrollBehavior: 'contain',
                    }),
                },
            }}
        >
            {children}
        </Popper>
    );
};

// The complexity analyzer misparses a self-closing SVG-only JSX return.
const PageSizeArrow = (props: SvgIconProps): JSX.Element =>
    React.createElement(ArrowDropDown, {
        ...props,
        style: { ...props.style, width: 24, height: 24, right: 8 },
        sx: { color: 'text.secondary', '&.Mui-disabled': { color: 'action.disabled' } },
    });

type LoadMorePageSizeSelectorProps = {
    enabled?: boolean;
    label?: React.ReactNode;
    pageSize: number;
    disabled?: boolean;
    onChange: (pageSize: number) => void;
};

const LoadMorePageSizeSelector = ({
    enabled,
    label = <CustomTransComponent translationKey="ALARM_TABLE:LOAD_AT_A_TIME" defaultValue="Load at a Time" />,
    pageSize,
    disabled,
    onChange,
}: LoadMorePageSizeSelectorProps): JSX.Element | null => {
    const labelId = React.useId();
    if (!enabled) return null;

    return (
        <Stack direction="row" alignItems="center" sx={{ gap: '8px' }}>
            <Typography
                id={labelId}
                variant="body2"
                color="text.primary"
                sx={{ whiteSpace: 'nowrap', textAlign: 'right', lineHeight: 'normal' }}
            >
                {label}
            </Typography>
            <FormControl variant="filled" hiddenLabel sx={{ width: 88, flexShrink: 0 }}>
                <Select<number>
                    labelId={labelId}
                    IconComponent={PageSizeArrow}
                    value={pageSize}
                    disabled={disabled}
                    onChange={(event: SelectChangeEvent<number>): void => onChange(Number(event.target.value))}
                    SelectDisplayProps={{ style: { padding: '9px 40px 9px 16px', lineHeight: '22px' } }}
                    sx={{
                        height: 40,
                        borderRadius: '4px 4px 0 0',
                        '&::before': { borderBottomColor: 'divider' },
                    }}
                >
                    {[5, 10, 25].map((size) => (
                        <MenuItem key={size} value={size}>
                            {size}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Stack>
    );
};

type PaginationStyles = {
    footer: SxProps<Theme>;
    count: SxProps<Theme>;
    button: SxProps<Theme>;
};

const getPaginationStyles = (showPageSizeSelector?: boolean): PaginationStyles => ({
    footer: showPageSizeSelector
        ? {
              minHeight: 50,
              flexShrink: 0,
              px: '24px',
              columnGap: '64px',
              fontFeatureSettings: "'liga' off, 'clig' off",
          }
        : { m: 2, columnGap: '40px' },
    count: showPageSizeSelector
        ? { color: 'text.primary', whiteSpace: 'nowrap', lineHeight: 'normal' }
        : { color: 'black', whiteSpace: 'nowrap' },
    button: showPageSizeSelector
        ? {
              height: 36,
              px: '16px',
              borderRadius: '4px',
              borderColor: 'primary.main',
              lineHeight: 'normal',
              flexShrink: 0,
          }
        : { width: 130 },
});

/**
 * The above type defines the props for a custom table component in TypeScript React, including options
 * for success/error states, pagination, loading state, data and headers, and various customization
 * options.
 * @property {string} wrapperClass - The `wrapperClass` property is a string that represents the CSS
 * class name(s) to be applied to the wrapper element of the custom table component.
 * @property {string} containerClass - A string representing the CSS class name for the container
 * element of the table.
 * @property {boolean} isPagination - A boolean value indicating whether pagination should be enabled
 * for the table.
 * @property {any[]} data - An array of data that will be displayed in the table.
 * @property {any[]} headers - An array of objects representing the headers of the table. Each object
 * should have properties like "label" (string) for the header label and "key" (string) for the
 * corresponding key in the data array.
 * @property {string} keyToTraverse - The `keyToTraverse` property is a string that represents the key
 * in the `data` array that should be used to render the table rows. This key is used to access the
 * corresponding value in each object within the `data` array.
 * @property {boolean} isLoading - A boolean value indicating whether the data is currently being
 * loaded or not.
 * @property noDataFoundIcon - The `noDataFoundIcon` property is an optional JSX element that
 * represents the icon to be displayed when there is no data found in the table.
 * @property {string} noDataFoundTitle - The `noDataFoundTitle` property is a string that represents
 * the title or heading to be displayed when there is no data available for the table.
 * @property {string} noDataFoundButtonText - The `noDataFoundButtonText` property is an optional
 * string that represents the text to be displayed on the button when no data is found in the table.
 * @property noDataFoundButtonAction - The `noDataFoundButtonAction` property is a function that will
 * be called when the button in the "no data found" section is clicked. It is an optional property and
 * can be used to define a custom action to be performed when the button is clicked.
 * @property handleCheckboxSelect - A function that is called when checkboxes in the table are
 * selected. It takes an array of strings as an argument, which represents the selected checkboxes.
 * @property handleFilterChange - A function that is called when a filter is changed in the table. It
 * takes four arguments: arg0 (the filter value), arg1 (the filter type), arg3 (the filter column), and
 * arg4 (the filter operator).
 * @property {number} total - The `total` property is a number that represents the total number of
 * items in the data set.
 * @property {string | JSX.Element} noDataFoundDescription - The `noDataFoundDescription` property is a
 * string or JSX element that represents the description or additional information to be displayed when
 * there is no data found in the table. It can be used to provide context or instructions to the user
 * when there are no records available to display.
 */

export const CustomTableLoadMore = memo(
    React.forwardRef((props: CustomTableLoadMoreProps, ref) => {
        const [filteredValues, setFilteredValues] = React.useState<any>({});
        const [sortedData, setSortedData] = React.useState<any>({});
        const [activeSortedAccessor, setActiveSortedAccessor] = React.useState<string | undefined>();
        const [filterAnchorEl, setFilterAnchorEl] = React.useState<HTMLElement | null>(null);
        const [activeFilterAccessor, setActiveFilterAccessor] = React.useState<string | undefined>();
        const filterPopupRef = React.useRef<HTMLDivElement | null>(null);
        const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
        const [openFilterDropDown, setOpenFilterDropDown] = React.useState<any>({});
        const [pageSize, setPageSize] = React.useState<number>(props.pageSize ?? 10);
        const theme = useTheme();
        const paginationStyles = getPaginationStyles(props.showPageSizeSelector);
        /* The below code is using the `usePagination` hook to manage pagination functionality in a
    TypeScript React component. */
        const { currentPage, setCurrentPage } = usePagination({
            pageSize: pageSize,
            totalItems: props?.total,
            currentPage: props?.currentPage || 0,
        });

        const [selectedIds, setSelectedIds, selectionHandler] = useSelectedIds();
        const allRowsSelected = Boolean(selectedIds.length) && selectedIds.length === props.data?.length;
        const someRowsSelected = Boolean(selectedIds.length) && selectedIds.length < props.data?.length;

        const isMount = useIsMount();

        React.useEffect(() => {
            if (!filterAnchorEl) return;

            const handleOutsideFilterClick = (event: MouseEvent): void => {
                const target = event.target as Node;
                if (filterAnchorEl.contains(target) || filterPopupRef.current?.contains(target)) return;

                setOpenFilterDropDown({});
                setFilterAnchorEl(null);
                setActiveFilterAccessor(undefined);
            };

            document.addEventListener('mousedown', handleOutsideFilterClick);
            return (): void => document.removeEventListener('mousedown', handleOutsideFilterClick);
        }, [filterAnchorEl]);

        /* The below code is using the `useImperativeHandle` hook from React to expose certain functions to
    the parent component through a ref. */
        React.useImperativeHandle(ref, () => ({
            resetCheckboxes: (): void => setSelectedIds([]),
            resetFilters: (key: string, type?: any): void => resetFilters(key, type),
            dropDownHandler: (key: string, type?: any): void => dropDownHandler(key, type),
        }));

        /* The below code is a React useEffect hook that is triggered when the component mounts. It checks
    if the `props.headers` array exists and has a length greater than 0. If it does, it iterates
    over each `header` object in the array. If the `header` object has a property `isFilterable`
    that is truthy, it sets the state of `openFilterDropDown` using the `setOpenFilterDropDown`
    function. The state is updated by spreading the previous state (`prev`) and setting the value of
    the `header.accessor` property to `false`. */
        React.useEffect(() => {
            if (props?.headers?.length) {
                props.headers.forEach((header: Header) => {
                    if (header?.isFilterable) {
                        setOpenFilterDropDown((prev: any) =>
                            header.accessor ? { ...prev, [header?.accessor]: false } : prev
                        );
                    }
                });
            }
        }, []);

        /* The below code is using the `useEffect` hook from React to execute a function whenever the
    `selectedIds` variable changes. The function being executed is
    `props.handleCheckboxSelect(selectedIds)`, which is passed as a prop to the component. This code
    is likely used to handle the selection of checkboxes and update the selectedIds state in the
    parent component. */
        React.useEffect(() => {
            if (props.handleCheckboxSelect) props.handleCheckboxSelect(selectedIds);
        }, [selectedIds]);

        /* The below code is using the `useEffect` hook from React to execute a function whenever the
    dependencies (`filteredValues`, `currentPage`, `pageSize`, `sortedData`) change. */
        React.useEffect(() => {
            if ((filteredValues || sortedData) && props.handleFilterChange) {
                props.handleFilterChange(filteredValues, sortedData);
            }
        }, [filteredValues, sortedData]);

        React.useEffect(() => {
            if ((currentPage || pageSize) && props.handlePageChange) {
                props.handlePageChange(currentPage, pageSize);
            }
        }, [currentPage, pageSize]);

        React.useEffect(() => {
            if (!isMount && props.controlledPageSize !== currentPage) {
                setCurrentPage(props.controlledPageSize);
            }
        }, [props.controlledPageSize]);

        React.useEffect(() => {
            if (props.pageSize) {
                setPageSize(props.pageSize);
            }
        }, [props.pageSize]);

        const handlePageSizeChange = (nextPageSize: number): void => {
            setPageSize(nextPageSize);
            setCurrentPage(0);
        };

        /**
         * The function `filterPayloadHandler` handles filtering of values based on an ID and value, and
         * updates the filtered values accordingly.
         * @param {any} id - The `id` parameter is used to identify the specific filter that is being
         * applied. It could be a unique identifier or a key that represents a specific filter option.
         * @param {any} value - The `value` parameter is the value that is being filtered. It is used to
         * determine whether to reset the filters or set the filtered values.
         */
        const filterPayloadHandler = (id: any, value: any): void => {
            if (id !== '') {
                if (value === '') {
                    resetFilters(id);
                } else {
                    setFilteredValues((prev: any) => ({ ...prev, [id]: value }));
                }
            }
        };

        /**
         * The function `dropDownHandler` toggles the state of a dropdown menu based on a given key and
         * type.
         * @param {string | undefined} key - The `key` parameter is a string or undefined. It is used to
         * identify the specific dropdown that needs to be toggled or opened.
         * @param {string} [type] - The `type` parameter is an optional string that specifies the action to
         * be performed on the dropdown. If the `type` is set to `'remove'`, it will call the
         * `resetFilters` function with the `key` as an argument.
         */
        const dropDownHandler: (key: string | undefined, type?: string) => void = (
            key: string | undefined,
            type?: string
        ): void => {
            if (!key) return;

            const hasDropDownKey = Object.keys(openFilterDropDown).includes(key);
            if (!hasDropDownKey) {
                setOpenFilterDropDown((prev: any) => ({ ...prev, [key]: true }));
                return;
            }

            if (type === 'remove') {
                resetFilters(key);
                if (activeFilterAccessor === key) {
                    setFilterAnchorEl(null);
                    setActiveFilterAccessor(undefined);
                }
            }

            if (type === 'close') {
                setOpenFilterDropDown((prev: any) => ({ ...prev, [key]: false }));
                if (activeFilterAccessor === key) {
                    setFilterAnchorEl(null);
                    setActiveFilterAccessor(undefined);
                }
                return;
            }

            setOpenFilterDropDown((prev: any) => ({ ...prev, [key]: !prev[key] }));
        };

        const toggleFilterHandler = (accessor: string, anchor: HTMLElement): void => {
            const isOpen = activeFilterAccessor === accessor && openFilterDropDown?.[accessor];
            if (isOpen) {
                setOpenFilterDropDown({});
                setFilterAnchorEl(null);
                setActiveFilterAccessor(undefined);
                return;
            }

            setOpenFilterDropDown({ [accessor]: true });
            setFilterAnchorEl(anchor);
            setActiveFilterAccessor(accessor);
        };

        /**
         * The function `handleSelectAll` is used to handle the selection of all items in a list.
         * @param {any} e - The parameter `e` is an event object that is passed to the `handleSelectAll`
         * function. It is typically an event object that is triggered when a checkbox is checked or
         * unchecked.
         */
        const handleSelectAll = (e: any): void => {
            let IdsArr: string[] = [];
            if (e.target.checked) {
                props?.data?.forEach((subData: any) => {
                    IdsArr.push(subData?.[props?.keyToTraverse]);
                });
            } else {
                IdsArr = [];
            }
            setSelectedIds(IdsArr);
        };

        /**
         * The function `handleSorting` is used to toggle between ascending and descending sorting based on
         * a given key.
         * @param {any} key - The `key` parameter is used to specify the property or key by which the data
         * should be sorted.
         */
        const handleSorting = (key: string, sortKey: string = key): void => {
            const isSameColumn = key === activeSortedAccessor;
            const sortType = isSameColumn && sortedData?.sortType !== 'DESC' ? 'DESC' : 'ASC';

            setActiveSortedAccessor(key);
            setSortedData({ key: sortKey, sortType });
        };

        /**
         * The function `handleSetFilteredValues` updates the `filteredValues` state by removing a specific
         * value from a specific filter, and also removes the filter if it becomes empty or if the value is
         * 'all'.
         * @param {string} id - The `id` parameter is a string that represents the identifier of a filter.
         * @param {any} value - The `value` parameter is the value that needs to be removed from the
         * `filteredValues` object.
         */
        const handleSetFilteredValues = (id: string, value: any): void => {
            const filtersClone = JSON.parse(JSON.stringify(filteredValues));
            if (filtersClone?.[id]) {
                const removableIndex = filtersClone[id]?.findIndex(
                    (filterValue: any) => String(filterValue) === String(value)
                );
                if (removableIndex !== -1) {
                    filtersClone[id].splice(removableIndex, 1);
                }
            }
            if ((filtersClone && !filtersClone[id].length) || value === 'all') {
                delete filtersClone[id];
            }
            setFilteredValues(filtersClone);
        };

        /**
         * The function handles the change event of a checkbox and updates the filtered values based on the
         * checkbox's state.
         * @param e - The `e` parameter is of type `React.ChangeEvent<HTMLInputElement>`, which represents
         * the event object generated when the checkbox is changed. It contains information about the
         * checkbox element and its new state.
         * @param {any} id - The `id` parameter is used to identify the checkbox element. It is typically a
         * unique identifier or key associated with the checkbox.
         * @param {any} value - The `value` parameter represents the value of the checkbox that was
         * clicked.
         * @param {any} [allValues] - The `allValues` parameter is an optional parameter that represents an
         * array of all possible values for the checkbox group.
         */
        const checkboxPayloadHandler = (
            e: React.ChangeEvent<HTMLInputElement>,
            id: any,
            value: any,
            allValues?: any
        ): void => {
            if (!e.target.checked) {
                handleSetFilteredValues(id, value);
            } else if (value === 'all') {
                setFilteredValues((prev: any) => ({ ...prev, [id]: allValues?.filter((val: any) => val !== 'all') }));
            } else {
                setFilteredValues((prev: any) => ({
                    ...prev,
                    [id]: prev[id]?.some((filterValue: any) => String(filterValue) === String(value))
                        ? prev[id]
                        : prev[id]
                        ? prev[id]?.concat([value])
                        : [value],
                }));
            }
        };

        const singleSelectionPayloadHandler = (accessor: string, value: any): void => {
            setFilteredValues((prev: any) => {
                const filtersClone = JSON.parse(JSON.stringify(prev ?? {}));
                if (value === 'all') {
                    delete filtersClone[accessor];
                } else {
                    filtersClone[accessor] = [value];
                }
                return filtersClone;
            });
        };

        /**
         * The function checks if a specific value is already checked in a filtered list.
         * @param {string} id - The `id` parameter is a string that represents the identifier of an item.
         * @param {any} accessor - The `accessor` parameter is a variable that represents the property or
         * key of an object. It is used to access a specific value within the object.
         * @param {any} value - The `value` parameter is the total number of items that should be checked.
         * @returns a boolean value. It returns `true` if the conditions inside the if statements are met,
         * and `false` otherwise.
         */
        const alreadyCheckedHandler = (id: any, accessor: any, value: any, isSingleSelect = false): any => {
            if (filteredValues && accessor) {
                if (isSingleSelect) {
                    if (id === 'all') {
                        return !Array.isArray(filteredValues?.[accessor]) || !filteredValues?.[accessor]?.length;
                    }
                    if (
                        Array.isArray(filteredValues?.[accessor]) &&
                        filteredValues?.[accessor]?.some((filterValue: any) => String(filterValue) === String(id))
                    ) {
                        return true;
                    }
                    return false;
                }

                if (id === 'all') {
                    if (filteredValues[accessor]?.length === value - 1) return true;
                }
                if (
                    Array.isArray(filteredValues?.[accessor]) &&
                    filteredValues?.[accessor]?.some((filterValue: any) => String(filterValue) === String(id))
                ) {
                    return true;
                }
                return false;
            }
        };

        /**
         * The function `resetFilters` is used to remove filters from a set of filtered values in a React
         * application.
         * @param {string} key - The key parameter is a string that represents the key of the filter to be
         * reset.
         * @param {any} [type] - The `type` parameter is an optional parameter of type `any`.
         */
        const resetFilters = (key: string, type?: any): void => {
            const filtersClone = JSON.parse(JSON.stringify(filteredValues));
            if (type) {
                setOpenFilterDropDown((prev: any) => ({ ...prev, [key]: false }));
            }
            if (key === 'globalSearch') {
                type.forEach((filter: any) => {
                    delete filtersClone[filter];
                    setOpenFilterDropDown((prev: any) => ({ ...prev, [filter]: false }));
                });
            } else {
                delete filtersClone[key];
            }
            setFilteredValues(filtersClone);
        };

        /* The below code is defining a function called `getTableBodyCellContent` that takes two
    parameters: `header` and `dataObj`. */
        const getTableBodyCellContent = (header: any, dataObj: any): JSX.Element => {
            if (header?.isSelectable) {
                return (
                    <Checkbox
                        color="primary"
                        checked={selectedIds.includes(dataObj?.[props?.keyToTraverse])}
                        onChange={selectionHandler(dataObj?.[props?.keyToTraverse])}
                    />
                );
            } else if (header?.cell) {
                return header?.cell(dataObj);
            }
            return (
                <Typography variant={header?.typeVariant ?? 'body1'}>
                    {dataObj?.[header?.accessor || ''] || 'N/A'}
                </Typography>
            );
        };

        /* The below code is a TypeScript React function called `getHeaderFilterBody`. It takes a `header`
    parameter of type `any` and returns a JSX element. */
        const renderLMCheckboxFilter = (header: TableFilterHeader, filterOptions: TableFilterOption[]): JSX.Element => {
            const isSingleSelectFilter = header?.filterSelectionType === 'single';
            const handleChangeCheckbox =
                (value: TableFilterValue) =>
                (e: React.ChangeEvent<HTMLInputElement>): void =>
                    checkboxPayloadHandler(
                        e,
                        header?.accessor,
                        value,
                        filterOptions.map((option: TableFilterOption) => option.id)
                    );

            return (
                <>
                    {filterOptions.map((option: TableFilterOption) => (
                        <Box key={String(option.id)}>
                            <Box
                                key={String(option.id)}
                                className={option.id === 'all' ? 'border-bottom-1' : undefined}
                                sx={{
                                    width: 'max-content',
                                    minWidth: 'min(200px, calc(100vw - 32px))',
                                    maxWidth: 'calc(100vw - 32px)',
                                }}
                            >
                                <MenuItem
                                    data-testid={`filter-option-${String(option.id)}`}
                                    value={String(option.id)}
                                    sx={{
                                        whiteSpace: 'normal',
                                        '& .MuiCheckbox-root, & .MuiRadio-root': { flexShrink: 0 },
                                    }}
                                >
                                    {isSingleSelectFilter ? (
                                        <Radio
                                            className="margin-right-16"
                                            checked={alreadyCheckedHandler(
                                                option.id,
                                                header?.accessor,
                                                filterOptions.length,
                                                true
                                            )}
                                            onChange={(): void =>
                                                singleSelectionPayloadHandler(header?.accessor ?? '', option.id)
                                            }
                                        />
                                    ) : (
                                        <Checkbox
                                            className="margin-right-16"
                                            id={String(option.id)}
                                            checked={alreadyCheckedHandler(
                                                option.id,
                                                header?.accessor,
                                                filterOptions.length
                                            )}
                                            onChange={handleChangeCheckbox(option.id)}
                                        />
                                    )}
                                    <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                        {option.label}
                                    </Box>
                                </MenuItem>
                            </Box>
                        </Box>
                    ))}
                </>
            );
        };

        const renderLMSearchFilter = (header: any): JSX.Element => (
            <Paper className="padding-16">
                {' '}
                <FilledInput
                    size="small"
                    placeholder="Search"
                    data-testid={`column-search-${header?.accessor}`}
                    className="bg-transparent border-1 border-bottom-none width-200"
                    id={header?.accessor}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                        filterPayloadHandler(e.target.id, e.target.value)
                    }
                    value={filteredValues[header?.accessor] ?? ''}
                    hiddenLabel={true}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                edge="end"
                                onClick={(): void => dropDownHandler(header?.accessor, 'remove')}
                            >
                                {filteredValues[header?.accessor] ? <HighlightOffIcon /> : <SearchIcon />}
                            </IconButton>
                        </InputAdornment>
                    }
                />
            </Paper>
        );

        const getHeaderFilterBody = (header: any): JSX.Element => {
            const filterOptions = header?.filterOptions;
            if (filterOptions?.length) {
                return renderLMCheckboxFilter(header, filterOptions);
            }
            if (header?.headerOptions) {
                return header?.headerOptions();
            }
            return renderLMSearchFilter(header);
        };
        const renderTableBody = (): JSX.Element | null => {
            if (props.isLoading) return <TableSkeleton headers={props.headers} />;
            if (!props?.data) return null;
            if (props?.data?.length)
                return (
                    <TableBody>
                        {props?.data?.map((dataObj: any) => (
                            <TableRow
                                className="position-relative"
                                key={dataObj?.[props?.keyToTraverse]}
                                data-testid={`table-row-${dataObj?.[props?.keyToTraverse]}`}
                            >
                                {props.headers?.map(
                                    (header: Header, columnIndex: number): JSX.Element => (
                                        <TableCell
                                            sx={getTableCellSx(header, dataObj)}
                                            key={header.accessor ?? `column-${columnIndex}`}
                                            className="tableHeadCell"
                                            data-testid={`cell-${header?.accessor}-${dataObj?.[props?.keyToTraverse]}`}
                                        >
                                            {getTableBodyCellContent(header, dataObj)}
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                );
            return (
                <TableBody>
                    <TableRow>
                        <TableCell colSpan={7}>
                            <EmptyState
                                className="emptyStateWrapper"
                                icon={props?.noDataFoundIcon}
                                title={props?.noDataFoundTitle}
                                {...(props?.noDataFoundDescription && {
                                    description: props.noDataFoundDescription,
                                })}
                                actions={
                                    props?.noDataFoundButtonAction && (
                                        <Button
                                            sx={{
                                                backgroundColor: theme?.palette?.primary?.main,
                                                '&:hover': {
                                                    backgroundColor: theme?.palette?.primary?.main,
                                                },
                                            }}
                                            variant={'contained'}
                                            color={'primary'}
                                            startIcon={<Add />}
                                            onClick={props?.noDataFoundButtonAction}
                                        >
                                            {props?.noDataFoundButtonText ?? ''}
                                        </Button>
                                    )
                                }
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            );
        };

        const isHeaderSorted = (header: Header): boolean => {
            const sortedKey = sortedData?.key;
            if (!sortedKey) return false;
            return activeSortedAccessor
                ? activeSortedAccessor === header?.accessor
                : sortedKey === header?.accessor || sortedKey === header?.sortKey;
        };

        const getHeaderSortDirection = (header: Header): 'asc' | 'desc' =>
            isHeaderSorted(header) && sortedData?.sortType?.toLowerCase() === 'desc' ? 'desc' : 'asc';

        const renderFilterPopup = (header: Header): JSX.Element | null => {
            if (!header.accessor || activeFilterAccessor !== header.accessor || !openFilterDropDown[header.accessor])
                return null;
            return (
                <TableFilterPopper
                    anchorEl={filterAnchorEl}
                    tableBoundary={header.filterBoundary === 'table' ? tableContainerRef.current : undefined}
                    placement={header.filterPlacement ?? 'bottom-start'}
                    offset={header.filterOffset}
                    zIndex={theme.zIndex.modal}
                    popupRef={filterPopupRef}
                >
                    <Paper
                        elevation={header.filterElevation ?? 1}
                        className="table-search-bar"
                        data-testid={`column-filter-popup-${header.accessor}`}
                        sx={{
                            ...(header.filterOptions && {
                                maxHeight: 'min(400px, calc(100dvh - 96px))',
                                overflowY: 'auto',
                                overscrollBehaviorY: 'contain',
                            }),
                        }}
                    >
                        <FormControl
                            variant="filled"
                            className={header.wrapperClass}
                            size="small"
                            defaultValue="Small"
                            {...((header.filterOptions || header.headerOptions) && {
                                sx: { ...header.headerSx?.(), padding: '0 !important' },
                            })}
                        >
                            {getHeaderFilterBody(header)}
                        </FormControl>
                    </Paper>
                </TableFilterPopper>
            );
        };

        const renderPagination = (): JSX.Element => (
            <Box alignItems="center" justifyContent="flex-end" display="flex" sx={paginationStyles.footer}>
                <LoadMorePageSizeSelector
                    enabled={props.showPageSizeSelector}
                    label={props.pageSizeSelectorLabel}
                    pageSize={pageSize}
                    disabled={props.isFetching}
                    onChange={handlePageSizeChange}
                />
                <Typography variant="body1" sx={paginationStyles.count}>
                    <CustomTransComponent translationKey="ALARM_TABLE:LOADED_LABEL" defaultValue="Loaded" />{' '}
                    <Box component="span" fontWeight={700}>
                        {props.data?.length}
                    </Box>{' '}
                    <CustomTransComponent translationKey="ALARM_TABLE:OF_LABEL" />{' '}
                    <Box component="span" fontWeight={600}>
                        {props.total}
                    </Box>
                </Typography>
                <Button
                    variant="outlined"
                    data-testid="LoadMorePaginationButton"
                    sx={paginationStyles.button}
                    disabled={props.isFetching || currentPage >= Math.ceil(props.total / pageSize) - 1}
                    onClick={(): void => setCurrentPage(currentPage + 1)}
                >
                    {props.isFetching && <CircularProgress size={14} sx={{ marginRight: 1 }} />}
                    <CustomTransComponent translationKey="ALARM_TABLE:LODE_MORE_LABEL" />
                </Button>
            </Box>
        );

        return (
            <>
                <TableContainer ref={tableContainerRef} className={props.wrapperClass ?? ''}>
                    <Table stickyHeader aria-label="sticky table" className={props.containerClass}>
                        <TableHead>
                            <TableRow>
                                {props.headers?.map((header: Header, columnIndex: number) => {
                                    /* Derived once per header so the sorted-key comparison and
                                       direction lookup are not repeated across the cell and label. */
                                    const isSorted = isHeaderSorted(header);
                                    const sortDirection = getHeaderSortDirection(header);

                                    return (
                                        <TableCell
                                            key={header.accessor ?? `column-${columnIndex}`}
                                            className="tableHeadCell"
                                            width={header.width}
                                            sx={getTableCellSx(header)}
                                            sortDirection={isSorted ? sortDirection : false}
                                            data-testid={`column-header-${header.accessor}`}
                                        >
                                            {header.isSelectable ? (
                                                <Checkbox
                                                    color="primary"
                                                    inputProps={{
                                                        'aria-label': 'select all rows',
                                                    }}
                                                    checked={allRowsSelected}
                                                    indeterminate={someRowsSelected}
                                                    onChange={handleSelectAll}
                                                />
                                            ) : (
                                                <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
                                                    {header.header}
                                                    {header.isSortable && (
                                                        <TableSortLabel
                                                            data-testid={`column-sort-${header.accessor}`}
                                                            direction={sortDirection}
                                                            active={isSorted}
                                                            onClick={(): void =>
                                                                handleSorting(
                                                                    header.accessor ?? '',
                                                                    header.sortKey ?? header.accessor ?? ''
                                                                )
                                                            }
                                                        >
                                                            <Box component="span"></Box>
                                                        </TableSortLabel>
                                                    )}

                                                    {header.isFilterable && (
                                                        <>
                                                            <ArrowDropDown
                                                                data-testid={`column-filter-${header.accessor}`}
                                                                onClick={(
                                                                    event: React.MouseEvent<SVGSVGElement>
                                                                ): void => {
                                                                    const anchor = header.filterOffset
                                                                        ? event.currentTarget.closest('th')
                                                                        : event.currentTarget.parentElement;
                                                                    if (header.accessor && anchor) {
                                                                        toggleFilterHandler(header.accessor, anchor);
                                                                    }
                                                                }}
                                                            />
                                                            {renderFilterPopup(header)}
                                                        </>
                                                    )}
                                                    {header.extraOptions?.()}
                                                </Stack>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        {renderTableBody()}
                    </Table>
                </TableContainer>
                {props.isPagination && renderPagination()}
            </>
        );
    })
);
